import React, { useEffect, useRef, useState } from 'react'
import { useTrans } from 'hooks'
import { getImageUrl } from '@utils/index'
import { scrollTo } from '@utils/safe'
import { PaginaNode } from '@gql/graphql'
import { SizeTable } from '@components/molecules'
import { AcquistiOnlineLayout, Page, PowerBar } from 'components/organisms'
import styles from './GuidaTaglieTemplate.module.sass'

interface Props {
  pagina?: PaginaNode
}

const GuidaTaglieTemplate = (props: Props) => {
  const { pagina = undefined } = props

  const t = useTrans()
  const powerBarWrapRef = useRef(null)
  const itemsRef = useRef([])
  const [activeItemId, setActiveItemId] = useState(undefined)

  const items = [
    {
      item: {
        pk: 1,
        name: t('Conversione taglie abbigliamento'),
      },
      headers: [
        { label: 'International' },
        { label: 'XS' },
        { label: 'S' },
        { label: 'M' },
        { label: 'L' },
        { label: 'XL' },
        { label: 'XXL' },
        { label: '3XL' },
      ],
      rows: [
        {
          header: 'I',
          values: [
            [38, 40],
            [42, 44],
            [46, 48],
            [50, 52],
            [54, 56],
            [58, 60],
            [62, 64],
          ],
        },
        {
          header: 'F',
          values: [
            [34, 36],
            [38, 40],
            [42, 44],
            [46, 48],
            [50, 52],
            [54, 56],
            [58, 60],
          ],
        },
        {
          header: 'D',
          values: [
            [42, 44],
            [46, 48],
            [50, 52],
            [54, 56],
            [58, 60],
            [62, 64],
            [66, 68],
          ],
        },
        {
          header: 'E',
          values: [
            [34, 36],
            [38, 40],
            [42, 44],
            [46, 48],
            [50, 52],
            [54, 56],
            [58, 60],
          ],
        },
        {
          header: 'U.K.',
          values: [
            [24, 26],
            [28, 30],
            [32, 34],
            [36, 38],
            [40, 42],
            [44, 46],
            [48, 50],
          ],
        },
        {
          header: 'U.S.A.',
          values: [
            [24, 26],
            [28, 30],
            [32, 34],
            [36, 38],
            [40, 42],
            [44, 46],
            [48, 50],
          ],
        },
      ].map((x, i) =>
        [
          {
            type: 'header' as 'header' | '',
            content: <>{x.header}</>,
          },
        ].concat(
          x.values.map((y, j) => ({
            type: undefined,
            content: (
              <span className={styles.cellValues}>
                {y.map((z, k) => (
                  <span key={`cell_${i}_${j}_${k}`}>{z}</span>
                ))}
              </span>
            ),
          }))
        )
      ),
    },
    {
      item: {
        pk: 2,
        name: t('Giacche e pantaloni antitaglio'),
        description: t('<p>Lunghezze in centimetri per taglia</p>'),
      },
      headers: [
        { label: t('Taglia'), colSpan: 3 },
        { label: 'XS' },
        { label: 'S' },
        { label: 'M' },
        { label: 'L' },
        { label: 'XL' },
        { label: 'XXL' },
        { label: '3XL' },
      ],
      rows: [
        {
          index: 1,
          values: [
            '84 - 88',
            '95 - 96',
            '100 - 104',
            '108 - 112',
            '116 - 120',
            '124 - 128',
            '132 - 136',
          ],
        },
        {
          index: 4,
          values: [
            '152 - 168',
            '156 - 174',
            '164 - 182',
            '174 - 190',
            '180 - 196',
            '184 - 200',
            '188 - 204',
          ],
        },
        {
          index: 2,
          values: [
            '68 - 72',
            '76 - 80',
            '84 - 88',
            '92 - 96',
            '100 - 104',
            '108 - 112',
            '116 - 120',
          ],
        },
        {
          index: 3,
          values: ['78', '80', '82', '84', '86', '86', '88'],
        },
        {
          index: 4,
          values: [
            '152 - 168',
            '156 - 174',
            '164 - 182',
            '174 - 190',
            '180 - 196',
            '184 - 200',
            '188 - 204',
          ],
        },
      ].map((x, i) =>
        (i === 0
          ? [
              {
                rowSpan: 5,
                content: (
                  <div className={styles.cellImages}>
                    <img
                      src={getImageUrl('taglie-giacche-pantaloni.png')}
                      style={{ width: '8.5rem', minWidth: '8.5rem' }}
                    />
                  </div>
                ),
                className: undefined,
              },
              {
                rowSpan: 5,
                content: (
                  <div className={styles.cellImages}>
                    <img
                      src={getImageUrl('taglie-giacche.png')}
                      style={{ width: '6.9rem', minWidth: '6.9rem' }}
                    />
                    <img
                      src={getImageUrl('taglie-pantaloni.png')}
                      style={{ width: '6.9rem', minWidth: '6.9rem' }}
                    />
                  </div>
                ),
                className: undefined,
              },
            ]
          : []
        )
          .concat([{ rowSpan: undefined, content: <>{x.index}</>, className: styles.rowIndex }])
          .concat(
            x.values.map((y) => ({ rowSpan: undefined, content: <>{y}</>, className: undefined }))
          )
      ),
    },
    {
      item: {
        pk: 3,
        name: t('Guanti'),
        description: t(
          '<p>Con un metro a nastro misurare la lunghezza della riga rossa e della circonferenza intorno alle nocche della mano e cercare la corrispondenza della taglia nella tabella.</p>'
        ),
      },
      headers: [{ label: t('Guanti') }, { label: t('Taglia') }, { label: '1' }, { label: '2' }],
      rows: [
        { values: ['XS', '17 cm', '17,5 - 19 cm'] },
        { values: ['S', '18 cm', '18,5 - 20 cm'] },
        { values: ['M', '19 cm', '19,5 - 21 cm'] },
        { values: ['L', '20 cm', '20,5 - 22 cm'] },
        { values: ['XL', '21 cm', '21 - 22,5 cm'] },
        { values: ['XXL', '22 cm', '22 - 23,5 cm'] },
      ].map((x, i) =>
        (i === 0
          ? [
              {
                rowSpan: 6,
                content: (
                  <div className={styles.cellImages}>
                    <img
                      src={getImageUrl('taglie-guanti.png')}
                      style={{ width: '17rem', minWidth: '17rem' }}
                    />
                  </div>
                ),
                className: undefined,
              },
            ]
          : []
        ).concat(
          x.values.map((y) => ({ rowSpan: undefined, content: <>{y}</>, className: undefined }))
        )
      ),
    },
    {
      item: {
        pk: 4,
        name: t('Scarponi'),
      },
      headers: [
        { label: t('Scarponi'), colSpan: 2 },
        { label: t('Forme') },
        { label: '40' },
        { label: '41' },
        { label: '42' },
        { label: '43' },
        { label: '44' },
        { label: '45' },
        { label: '46' },
        { label: '47' },
      ],
      rows: [
        {
          index: 1,
          forma: t('Lunghezza (cm)'),
          values: ['26,8', '27,5', '28,2', '28,9', '29,6', '30,3', '31', '31,7'],
        },
        {
          index: 2,
          forma: t('Larghezza (cm)'),
          values: ['9,6', '9,8', '10', '10,2', '10,4', '10,6', '10,8', '11'],
        },
      ].map((x, i) =>
        (i === 0
          ? [
              {
                rowSpan: 2,
                content: (
                  <div className={styles.cellImages}>
                    <img
                      src={getImageUrl('taglie-scarponi.png')}
                      style={{ width: '10.8rem', minWidth: '10.8rem' }}
                    />
                  </div>
                ),
                className: undefined,
              },
            ]
          : []
        )
          .concat([
            { rowSpan: undefined, content: <>{x.index}</>, className: styles.rowIndex },
            { rowSpan: undefined, content: <>{x.forma}</>, className: styles.rowIndex },
          ])
          .concat(
            x.values.map((y) => ({ rowSpan: undefined, content: <>{y}</>, className: undefined }))
          )
      ),
    },
  ]

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 10) {
        setActiveItemId(items[items.length - 1].item.pk)
        return
      }

      setActiveItemId(
        itemsRef?.current
          ?.map((ref, index) => ({
            pk: items[index].item.pk,
            value:
              ref.getBoundingClientRect().bottom >
              powerBarWrapRef.current?.getBoundingClientRect().bottom,
          }))
          .filter((x) => x.value)?.[0]?.pk
      )
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [itemsRef, powerBarWrapRef])

  const scrollToRef = (item) => {
    const index = items.map((x) => x.item.pk).indexOf(item.pk)
    if (typeof index !== 'undefined' && itemsRef?.current?.length > index) {
      scrollTo(
        itemsRef.current[index],
        20 + powerBarWrapRef.current?.getBoundingClientRect().height
      )
    }
  }

  return (
    <Page pagina={pagina} className={`${styles.root}`}>
      <AcquistiOnlineLayout pagina={pagina}>
        <div className={styles.scope}>
          <div className={styles.powerBarWrap} ref={powerBarWrapRef}>
            <PowerBar
              items={items.map((x) => ({
                pk: x.item.pk,
                name: x.item.name,
                selected: x.item.pk === activeItemId,
              }))}
              onItemClick={(item) => scrollToRef(item)}
              layout="boxed"
              className={styles.powerBar}
            />
          </div>
          <div className={styles.list}>
            {items.map((x, i) => (
              <div
                key={x.item.pk}
                className={styles.item}
                ref={(el) => {
                  itemsRef.current[i] = el
                }}
              >
                <SizeTable {...x} />
              </div>
            ))}
          </div>
        </div>
      </AcquistiOnlineLayout>
    </Page>
  )
}

export default GuidaTaglieTemplate
